import React from "react";
import {
  CountdownColorSlots,
  CountdownColors,
  CountdownConfig,
  MS_DAY,
  computeCountdownParts,
  formatCountdownText,
  isCountdownUrgent,
  pad,
  resolveCountdownColor,
  resolveShowWithinDays,
  useCountdownNow,
} from "../../components/Countdown";
import {
  CountdownWrapper,
  CountdownHeader,
  CountdownIcon,
  CountdownLabel,
  CountdownUnits,
  CountdownUnit,
  CountdownUnitBox,
  CountdownUnitLabel,
  CountdownText,
} from "./DynamicFormWithGallery.styles";
import { sanitizeSvg } from "../../utils/sanitize";

// Reexport para compatibilidade com testes/consumidores que importavam daqui.
export { isCountdownUrgent, resolveCountdownColor } from "../../components/Countdown";

const CLOCK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--icon-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';

interface CountdownSectionProps {
  config?: CountdownConfig;
  colors?: CountdownColors;
  unpublishAt?: string;
}

export const CountdownSection: React.FC<CountdownSectionProps> = ({
  config,
  colors,
  unpublishAt,
}) => {
  const showSeconds = config?.showSeconds !== false;
  const now = useCountdownNow(unpublishAt, showSeconds);

  if (config?.visible === false || !unpublishAt || now === null) return null;

  const target = new Date(unpublishAt).getTime();
  if (Number.isNaN(target)) return null;

  const diff = target - now;
  if (diff <= 0) return null;

  const daysRemaining = diff / MS_DAY;
  const showWithinDays = resolveShowWithinDays(config);
  if (showWithinDays > 0 && daysRemaining > showWithinDays) return null;

  const isUrgent = isCountdownUrgent(colors, daysRemaining);
  const color = (key: keyof CountdownColorSlots) =>
    resolveCountdownColor(colors, isUrgent, key);

  const { days, hours, minutes, seconds } = computeCountdownParts(diff);

  const inFinalDay = days < 1;
  const units = inFinalDay
    ? [
        { value: hours, label: "HRS" },
        { value: minutes, label: "MIN" },
        ...(showSeconds ? [{ value: seconds, label: "SEG" }] : []),
      ]
    : [
        { value: days, label: days === 1 ? "DIA" : "DIAS" },
        { value: hours, label: "HRS" },
        { value: minutes, label: "MIN" },
        ...(showSeconds ? [{ value: seconds, label: "SEG" }] : []),
      ];

  const text = config?.text ?? "";
  const hasText = text.trim().length > 0;
  const showIcon = config?.showIcon !== false;

  const orientation = config?.orientation || "horizontal";

  return (
    <CountdownWrapper
      $bg={color("bg")}
      $border={color("border")}
      $orientation={orientation}
      style={{ "--icon-color": color("label") } as React.CSSProperties}
    >
      {(showIcon || hasText) && (
        <CountdownHeader>
          {showIcon && (
            <CountdownIcon
              dangerouslySetInnerHTML={{ __html: sanitizeSvg(config?.iconSvg || CLOCK_SVG) }}
            />
          )}
          {hasText && (
            <CountdownLabel $color={color("label")}>{text}</CountdownLabel>
          )}
        </CountdownHeader>
      )}

      {config?.format === "text" ? (
        <CountdownText $color={color("numbers")}>
          {formatCountdownText(now, target)}
        </CountdownText>
      ) : (
        <CountdownUnits>
          {units.map((unit) => (
            <CountdownUnit key={unit.label}>
              <CountdownUnitBox
                $boxed={config?.boxed !== false}
                $boxBg={color("boxBg")}
                $boxText={color("boxText")}
                $numbers={color("numbers")}
              >
                {pad(unit.value)}
              </CountdownUnitBox>
              <CountdownUnitLabel $color={color("units")}>
                {unit.label}
              </CountdownUnitLabel>
            </CountdownUnit>
          ))}
        </CountdownUnits>
      )}
    </CountdownWrapper>
  );
};
